import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import formatISODate from '../../../Helpers/formatISODate'
import './styles.scss'

const TIMELINE = [
  { label: 'Shipment Created',  icon: solid('clipboard-list') },
  { label: 'Package Received',  icon: solid('box-open')       },
  { label: 'In Transit',        icon: solid('truck')          },
  { label: 'Delivered',         icon: solid('circle-check')   },
]

const STATUS_STEP = {
  'Pending':          0,
  'Picked up':        1,
  'In Transit':       2,
  'Enroute':          2,
  'On Hold':          2,
  'Out for Delivery': 2,
  'Delivered':        3,
}

const STATUS_BADGE = {
  'Delivered':  { color: '#2e7d32', bg: '#e8f5e9', border: '#a5d6a7' },
  'On Hold':    { color: '#e65100', bg: '#fff3e0', border: '#ffcc80' },
  'Cancelled':  { color: '#c62828', bg: '#ffebee', border: '#ef9a9a' },
  'Returned':   { color: '#6a1b9a', bg: '#f3e5f5', border: '#ce93d8' },
}

const isTerminal = (status) => status === 'Cancelled' || status === 'Returned'

const getTimelineSub = (index, data) => {
  switch (index) {
    case 0: return formatISODate(data.dispatchDate)?.split(' ')[0]
    case 1: return data.senderAddress
    case 2: return data.currentLocation
    case 3: return data.receiverAddress
    default: return null
  }
}

const InfoField = ({ label, value }) => (
  <div className="tir-field">
    <span className="tir-field__label">{label}</span>
    <span className="tir-field__value">{value || '—'}</span>
  </div>
)

const TrackItResult = ({ trackItResult, onReset }) => {
  const status      = trackItResult.status
  const currentStep = STATUS_STEP[status] ?? 0
  const badge       = STATUS_BADGE[status]
  const terminal    = isTerminal(status)

  return (
    <div className="tir">

      {/* ── Header ─────────────────────────────────────────── */}
      <div className="tir__header">
        <div className="tir__header__left">
          <span className="tir__label">Tracking Number</span>
          <h3 className="tir__tracking-no">{trackItResult.trackingNumber}</h3>
        </div>

        <div className="tir__header__right">
          {badge ? (
            <span
              className="tir__badge"
              style={{ color: badge.color, backgroundColor: badge.bg, borderColor: badge.border }}
            >
              {status}
            </span>
          ) : (
            <span className="tir__badge tir__badge--default">{status}</span>
          )}

          <button className="tir__reset" onClick={onReset}>
            <FontAwesomeIcon icon={solid('rotate-left')} />
            Track Another
          </button>
        </div>
      </div>

      {/* ── Route overview ──────────────────────────────────── */}
      <div className="tir-route">
        <div className="tir-route__endpoint">
          <div className="tir-route__endpoint__icon tir-route__endpoint__icon--origin">
            <FontAwesomeIcon icon={solid('circle-dot')} />
          </div>
          <span className="tir-route__endpoint__label">ORIGIN</span>
          <span className="tir-route__endpoint__name">{trackItResult.senderName || '—'}</span>
          <span className="tir-route__endpoint__addr">{trackItResult.senderAddress || '—'}</span>
        </div>

        <div className="tir-route__mid">
          <div className="tir-route__mid__line" />
          <div className="tir-route__mid__truck">
            <img src="/favicon.png" alt="Farze Global Logistics" />
          </div>
          <div className="tir-route__mid__line" />
          {trackItResult.currentLocation && (
            <p className="tir-route__mid__location">
              <FontAwesomeIcon icon={solid('location-dot')} />
              {trackItResult.currentLocation}
            </p>
          )}
        </div>

        <div className="tir-route__endpoint tir-route__endpoint--dest">
          <div className="tir-route__endpoint__icon tir-route__endpoint__icon--dest">
            <FontAwesomeIcon icon={solid('flag-checkered')} />
          </div>
          <span className="tir-route__endpoint__label">DESTINATION</span>
          <span className="tir-route__endpoint__name">{trackItResult.receiverName || '—'}</span>
          <span className="tir-route__endpoint__addr">{trackItResult.receiverAddress || '—'}</span>
        </div>
      </div>

      {/* ── Special state banners ───────────────────────────── */}
      {terminal && (
        <div className={`tir__banner tir__banner--${status.toLowerCase()}`}>
          <FontAwesomeIcon icon={status === 'Cancelled' ? solid('ban') : solid('rotate-left')} />
          {status === 'Cancelled'
            ? 'This shipment has been cancelled. Please contact us for assistance.'
            : 'This shipment is being returned to the sender.'}
        </div>
      )}

      {status === 'On Hold' && (
        <div className="tir__banner tir__banner--hold">
          <FontAwesomeIcon icon={solid('triangle-exclamation')} />
          This shipment is currently on hold. We will update you as soon as it resumes.
        </div>
      )}

      {/* ── Vertical journey timeline ───────────────────────── */}
      {!terminal && (
        <div className="tir-card">
          <div className="tir-card__header">
            <FontAwesomeIcon icon={solid('route')} />
            Shipment Journey
          </div>

          <div className="tir-timeline">
            {TIMELINE.map((step, i) => {
              const completed = i < currentStep
              const active    = i === currentStep
              const isLast    = i === TIMELINE.length - 1
              const sub       = (completed || active) ? getTimelineSub(i, trackItResult) : null

              return (
                <div
                  key={i}
                  className={`tir-timeline__item${completed ? ' completed' : ''}${active ? ' active' : ''}`}
                >
                  <div className="tir-timeline__left">
                    <div className="tir-timeline__dot">
                      {(completed || active) && <FontAwesomeIcon icon={step.icon} />}
                    </div>
                    {!isLast && <div className="tir-timeline__vline" />}
                  </div>

                  <div className="tir-timeline__body">
                    <div className="tir-timeline__row">
                      <span className="tir-timeline__label">{step.label}</span>
                      {active && <span className="tir-timeline__badge">Current</span>}
                    </div>
                    {sub && <span className="tir-timeline__sub">{sub}</span>}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ── Shipment details ────────────────────────────────── */}
      <div className="tir-card">
        <div className="tir-card__header">
          <FontAwesomeIcon icon={solid('box')} />
          Shipment Details
        </div>
        <div className="tir-card__body">
          <InfoField
            label="Dispatch Date"
            value={formatISODate(trackItResult.dispatchDate)?.split(' ')[0]}
          />
          <InfoField label="Current Location"     value={trackItResult.currentLocation}     />
          <InfoField label="Package Description"  value={trackItResult.packageDescription}  />
          <InfoField label="Status"               value={trackItResult.status}              />
        </div>
      </div>

    </div>
  )
}

export default TrackItResult
